/**
 * The catalogue of bearer-credential URL shapes, shared by every surface that must not keep or
 * show them: client telemetry (`client/src/utils/telemetry/access-key-redaction.ts`), outbound
 * email click tracking and the stored `SgEvent` click URLs (server).
 *
 * The attestation link, the lodgement summary and the insurer invoice view all resolve a record
 * by an unguessable key carried in the URL path; invite, password-reset and lodgement links carry
 * their secrets as a query parameter (`inviteSecret`, `resetSecret`, `accessKey`,
 * `summaryToken`). A signed `/r/<token>` email click redirect encodes its whole target URL, so
 * its token is treated as a credential too. Redaction replaces only the secret with {@link REDACTED_CREDENTIAL} and keeps
 * the route shape and every other parameter (`email` is an identifier, not a credential).
 *
 * Every pattern here is linear (no nested or overlapping quantifiers); keep it that way.
 */

export const REDACTED_CREDENTIAL = '[redacted]';

/**
 * Path shapes whose last segment is an access key. The captured prefix is preserved. A segment
 * ends at a path, query or fragment delimiter, and also at whitespace or a quote so the
 * patterns hold inside free text (breadcrumb messages, exception values, JSON fragments).
 */
export const CREDENTIAL_PATH_PATTERNS: readonly RegExp[] = [
    /(\/public\/lodgement-summary\/)[^/?#\s"'<>]+/i,
    /(\/public\/[^/?#\s"'<>]+\/invoice\/view\/)[^/?#\s"'<>]+/i,
    /(\/attestation\/)[^/?#\s"'<>]+/i,
    // A signed first-party click-tracking redirect, `/r/<base64url(json)>.<base64url(hmac)>`. The
    // body is encoded, not encrypted, and decodes to the target URL, so a token minted for an invite
    // or reset link before those were left unwrapped still carries the secret. The whole token goes.
    /(\/r\/)[\w-]+\.[\w-]+/i,
];

/** Query parameters that carry a secret. The captured `?name=` prefix is preserved. */
export const CREDENTIAL_QUERY_PARAM_PATTERN = /([?&](?:accessKey|inviteSecret|resetSecret|summaryToken)=)[^&#\s"'<>]*/i;

const GLOBAL_PATH_PATTERNS = CREDENTIAL_PATH_PATTERNS.map((pattern) => new RegExp(pattern.source, 'gi'));
const GLOBAL_QUERY_PATTERN = new RegExp(CREDENTIAL_QUERY_PARAM_PATTERN.source, 'gi');

/** Redact every access-key path segment and secret query parameter in a string. */
export function redactCredentialUrl(text: string): string {
    let result = text;
    for (const pattern of GLOBAL_PATH_PATTERNS) {
        result = result.replace(pattern, `$1${REDACTED_CREDENTIAL}`);
    }
    return result.replace(GLOBAL_QUERY_PATTERN, `$1${REDACTED_CREDENTIAL}`);
}

/**
 * Whether a URL is credential-bearing: its path addresses a record by an access key, or its query
 * carries a secret parameter. Already-redacted URLs still count, since the route shape is kept.
 */
export function isCredentialUrl(url: string): boolean {
    return CREDENTIAL_PATH_PATTERNS.some((pattern) => pattern.test(url)) || CREDENTIAL_QUERY_PARAM_PATTERN.test(url);
}

/** Whether a string still holds a credential value that {@link redactCredentialUrl} would remove. */
export function containsCredentialValue(text: string): boolean {
    return redactCredentialUrl(text) !== text;
}

const MAX_DEPTH = 8;

function isPlainObject(value: unknown): value is Record<string, unknown> {
    if (typeof value !== 'object' || value === null) {
        return false;
    }
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}

/**
 * Redact credentials in every string reachable from `value` through plain objects and arrays.
 * Other values (numbers, dates, class instances, functions) are returned unchanged. Returns
 * the same reference when nothing needed redacting, so untouched values are not re-allocated.
 */
export function redactCredentialUrlsDeep<T>(value: T, depth = 0): T {
    if (typeof value === 'string') {
        return redactCredentialUrl(value) as T;
    }
    if (depth >= MAX_DEPTH) {
        // Nothing below the cap is inspected, so nothing below it may leave either.
        return (Array.isArray(value) || isPlainObject(value) ? '[redacted: depth]' : value) as T;
    }
    if (Array.isArray(value)) {
        let changed = false;
        const next = value.map((item) => {
            const redacted = redactCredentialUrlsDeep(item, depth + 1);
            changed = changed || redacted !== item;
            return redacted;
        });
        return (changed ? next : value) as T;
    }
    if (isPlainObject(value)) {
        let changed = false;
        const next: Record<string, unknown> = {};
        for (const [key, item] of Object.entries(value)) {
            const redacted = redactCredentialUrlsDeep(item, depth + 1);
            changed = changed || redacted !== item;
            Object.defineProperty(next, key, { value: redacted, enumerable: true, writable: true, configurable: true });
        }
        return (changed ? next : value) as T;
    }
    return value;
}
